import React from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Icon,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha,
  Stack,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { EmptyState } from "../../../../../components/EmptyState/EmptyState";
import { PageHeader } from "../../../components/PageHeader/PageHeader";
import { IESDoc, FilterGroup, TableColumn } from "../../../../../app.interface";
import { DeleteConfirmationDialog } from "../../../components/DeleteConfirmationDialog/DeleteConfirmationDialog";
import { menuItemsService } from "../../../services/menu-items.service";
import { MenuItemDetailsDrawer } from "./MenuItemDetailsDrawer";
import { AppTable } from "../../../../../components/AppTable";
import { AppSearch } from "../../../../../components/AppSearch";
import dayjs from "dayjs";
import { hasPermission } from "../../../../auth/utils/permission.utils";
import { useAuth } from "../../../../auth/hooks/useAuth";

export const MenuItemsManager: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = React.useState<IESDoc[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState<IESDoc | null>(null);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [totalItems, setTotalItems] = React.useState(0);
  const [sortField, setSortField] = React.useState<string>("created_at");
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("desc");
  const [search, setSearch] = React.useState("");
  const [filters, setFilters] = React.useState<Record<string, any>>({});
  const [detailsDrawerOpen, setDetailsDrawerOpen] = React.useState(false);
  const [toggleDialogOpen, setToggleDialogOpen] = React.useState(false);
  const [selectedItemForToggle, setSelectedItemForToggle] =
    React.useState<IESDoc | null>(null);

  const filterGroups: FilterGroup[] = [
    {
      id: "category",
      label: "Category",
      type: "select",
      icon: "category",
      description: "Filter items by their category",
      options: [
        {
          id: "main_course",
          label: "Main Course",
          value: "main_course",
          icon: "restaurant",
          color: "#0891B2",
        },
        {
          id: "appetizer",
          label: "Appetizer",
          value: "appetizer",
          icon: "tapas",
          color: "#0891B2",
        },
        {
          id: "dessert",
          label: "Dessert",
          value: "dessert",
          icon: "cake",
          color: "#0891B2",
        },
        {
          id: "beverage",
          label: "Beverage",
          value: "beverage",
          icon: "local_bar",
          color: "#0891B2",
        },
      ],
    },
    {
      id: "status",
      label: "Status",
      type: "select",
      icon: "fiber_manual_record",
      description: "Filter items by their availability",
      options: [
        {
          id: "available",
          label: "Available",
          value: "available",
          icon: "check_circle",
          color: "#059669",
        },
        {
          id: "unavailable",
          label: "Unavailable",
          value: "unavailable",
          icon: "cancel",
          color: "#DC2626",
        },
      ],
    },
    {
      id: "sortBy",
      label: "Sort By",
      type: "select",
      icon: "sort",
      description: "Sort items by field",
      options: [
        {
          id: "name_asc",
          label: "Name (A-Z)",
          value: "name_asc",
          icon: "arrow_upward",
        },
        {
          id: "name_desc",
          label: "Name (Z-A)",
          value: "name_desc",
          icon: "arrow_downward",
        },
        {
          id: "price_asc",
          label: "Price (Low to High)",
          value: "price_asc",
          icon: "trending_up",
        },
        {
          id: "price_desc",
          label: "Price (High to Low)",
          value: "price_desc",
          icon: "trending_down",
        },
      ],
    },
  ];

  const MasterColumns: TableColumn[] = [
    {
      id: "name",
      header: "Item Details",
      field: "name",
      type: "action",
      width: "250px",
      renderAction: (row: any) => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {row.name}
          </Typography>
          {row.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {row.description}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            Created at: {dayjs(row.created_at).format("MMM D, YYYY")}
          </Typography>
        </Box>
      ),
    },
    {
      id: "category",
      header: "Category & Price",
      field: "category",
      type: "action",
      width: "200px",
      renderAction: (row: any) => (
        <Stack spacing={1}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: alpha("#0891B2", 0.08),
              color: "#0891B2",
              border: "1px solid",
              borderColor: alpha("#0891B2", 0.2),
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            <Icon sx={{ fontSize: 16 }}>restaurant_menu</Icon>
            {row.category || "Uncategorized"}
          </Box>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: alpha("#059669", 0.08),
              color: "#059669",
              border: "1px solid",
              borderColor: alpha("#059669", 0.2),
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            <Icon sx={{ fontSize: 16 }}>payments</Icon>
            {row.price?.toFixed(3)} BHD
          </Box>
        </Stack>
      ),
    },
    {
      id: "cost",
      header: "Cost & Profit",
      field: "total_cost",
      type: "action",
      width: "200px",
      renderAction: (row: any) => (
        <Stack spacing={1}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: alpha("#0891B2", 0.08),
              color: "#0891B2",
              border: "1px solid",
              borderColor: alpha("#0891B2", 0.2),
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            <Icon sx={{ fontSize: 16 }}>calculate</Icon>
            Cost: {row.total_cost?.toFixed(3)} BHD
          </Box>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: alpha("#059669", 0.08),
              color: "#059669",
              border: "1px solid",
              borderColor: alpha("#059669", 0.2),
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            <Icon sx={{ fontSize: 16 }}>trending_up</Icon>
            Profit: {(row.price - row.total_cost)?.toFixed(3)} BHD
          </Box>
        </Stack>
      ),
    },
    {
      id: "status",
      header: "Status",
      field: "is_available",
      type: "action",
      width: "150px",
      renderAction: (row: any) => (
        <Stack spacing={1}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: row.is_available
                ? alpha("#059669", 0.08)
                : alpha("#DC2626", 0.08),
              color: row.is_available ? "#059669" : "#DC2626",
              border: "1px solid",
              borderColor: row.is_available
                ? alpha("#059669", 0.2)
                : alpha("#DC2626", 0.2),
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            <Icon sx={{ fontSize: 16 }}>
              {row.is_available ? "check_circle" : "cancel"}
            </Icon>
            {row.is_available ? "Available" : "Unavailable"}
          </Box>
          <Tooltip
            title={row.is_available ? "Disable Item" : "Enable Item"}
            arrow
          >
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenToggleDialog(row);
              }}
              disabled={
                !hasPermission(
                  "inventory.menu_items.edit",
                  user?.newPermissions,
                  user?.assignment_details?.id,
                  user?.is_store_owner
                )
              }
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                px: 1,
                py: 0.5,
                borderRadius: 1,
                width: "100%",
                height: 24,
                bgcolor: row.is_available
                  ? alpha("#DC2626", 0.08)
                  : alpha("#059669", 0.08),
                color: row.is_available ? "#DC2626" : "#059669",
                fontSize: "0.75rem",
                border: "1px solid",
                borderColor: row.is_available
                  ? alpha("#DC2626", 0.2)
                  : alpha("#059669", 0.2),
                fontWeight: 500,
                "&:hover": {
                  bgcolor: row.is_available
                    ? alpha("#DC2626", 0.12)
                    : alpha("#059669", 0.12),
                },
              }}
            >
              <Icon sx={{ fontSize: 16 }}>
                {row.is_available ? "toggle_off" : "toggle_on"}
              </Icon>
              {row.is_available ? "Disable" : "Enable"}
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
    {
      id: "actions",
      header: "",
      field: "actions",
      type: "action",
      width: "100px",
      renderAction: (row: any) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Edit Item" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleEditItem(row);
              }}
              disabled={
                !hasPermission(
                  "inventory.menu_items.edit",
                  user?.newPermissions,
                  user?.assignment_details?.id,
                  user?.is_store_owner
                )
              }
              sx={{
                width: 34,
                height: 34,
                bgcolor: alpha("#0891B2", 0.08),
                color: "#0891B2",
                backdropFilter: "blur(8px)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-1px)",
                  bgcolor: alpha("#0891B2", 0.12),
                  boxShadow: "0 4px 8px rgba(8, 145, 178, 0.15)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              <Icon sx={{ fontSize: 18 }}>edit_outlined</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Item" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDeleteDialog(row);
              }}
              disabled={
                !hasPermission(
                  "inventory.menu_items.delete",
                  user?.newPermissions,
                  user?.assignment_details?.id,
                  user?.is_store_owner
                )
              }
              sx={{
                width: 34,
                height: 34,
                bgcolor: alpha("#DC2626", 0.08),
                color: "#DC2626",
                backdropFilter: "blur(8px)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-1px)",
                  bgcolor: alpha("#DC2626", 0.12),
                  boxShadow: "0 4px 8px rgba(220, 38, 38, 0.15)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              <Icon sx={{ fontSize: 18 }}>delete_outlined</Icon>
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  React.useEffect(() => {
    loadItems();
  }, [page, pageSize, sortField, sortOrder, search, filters]);

  const loadItems = async () => {
    try {
      setLoading(true);
      const response = await menuItemsService.listMenuItems({
        searchQuery: search,
        sortField,
        sortOrder,
        pageIndex: page,
        pageSize,
        ...filters,
      });
      setItems(response.data);
      setTotalItems(response.metadata.total.value);
    } catch (error) {
      toast.error("Failed to load menu items");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateItem = () => {
    navigate("/business/menu/menu-items/new");
  };

  const handleEditItem = (item: IESDoc) => {
    navigate(`/business/menu/menu-items/${item._id}/edit`);
  };

  const handleDeleteItem = async () => {
    if (!selectedItem) return;
    try {
      setSaving(true);
      await menuItemsService.deleteMenuItem(selectedItem._id);
      toast.success("Menu item deleted successfully");
      handleCloseDeleteDialog();
      loadItems();
    } catch (error) {
      toast.error("Failed to delete menu item");
      console.error("Error deleting menu item:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteDialog = (item: IESDoc) => {
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setSelectedItem(null);
    setDeleteDialogOpen(false);
  };

  const handleSort = (field: string) => {
    if (field === sortField) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleDetailsOpen = (item: IESDoc) => {
    setSelectedItem(item);
    setDetailsDrawerOpen(true);
  };

  const handleDetailsClose = () => {
    setSelectedItem(null);
    setDetailsDrawerOpen(false);
  };

  const handleOpenToggleDialog = (item: IESDoc) => {
    setSelectedItemForToggle(item);
    setToggleDialogOpen(true);
  };

  const handleCloseToggleDialog = () => {
    setSelectedItemForToggle(null);
    setToggleDialogOpen(false);
  };

  const handleToggleAvailability = async () => {
    if (!selectedItemForToggle) return;
    try {
      setSaving(true);
      await menuItemsService.toggleMenuItemAvailability(
        selectedItemForToggle._id
      );
      toast.success(
        `Menu item ${
          !selectedItemForToggle.is_available ? "enabled" : "disabled"
        } successfully`
      );
      handleCloseToggleDialog();
      loadItems();
    } catch (error) {
      toast.error("Failed to update menu item availability");
      console.error("Error updating menu item availability:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Menu Items"
        subtitle="Manage your restaurant's menu items"
        icon="restaurant_menu"
        actions={
          <Button
            variant="contained"
            startIcon={<Icon>add</Icon>}
            onClick={handleCreateItem}
            disabled={
              !hasPermission(
                "inventory.menu_items.create",
                user?.newPermissions,
                user?.assignment_details?.id,
                user?.is_store_owner
              )
            }
            sx={{
              bgcolor: "#0891B2",
              "&:hover": {
                bgcolor: "#0E7490",
              },
            }}
          >
            Add Menu Item
          </Button>
        }
      />

      <Paper
        sx={{
          p: 3,
          mb: 3,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid",
          borderColor: alpha("#0891B2", 0.1),
        }}
        elevation={0}
      >
        <AppSearch
          placeholder="Search menu items by name or description..."
          maxWidth="100%"
          onSearch={setSearch}
          onFilterChange={setFilters}
          filterGroups={filterGroups}
          defaultFilters={filters}
        />
      </Paper>

      <Paper
        sx={{
          p: 3,
          mb: 3,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid",
          borderColor: alpha("#0891B2", 0.1),
        }}
        elevation={0}
      >
        <AppTable
          data={items}
          columns={MasterColumns}
          loading={loading}
          sortConfig={{ field: sortField, direction: sortOrder }}
          onSort={handleSort}
          pagination={{
            total: totalItems,
            page: page,
            pageSize: pageSize,
            onPageChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          header={{
            title: "Menu Items List",
            subtitle: "Your current list of menu items",
          }}
          onRowClick={(row) => handleDetailsOpen(row)}
          styleOptions={{
            bordered: true,
            columnSeparators: true,
            headerBackground: alpha("#0891B2", 0.02),
            rowHoverBackground: alpha("#0891B2", 0.04),
            borderColor: alpha("#0891B2", 0.1),
          }}
          toolbarActions={{
            refresh: {
              label: "Refresh List",
              is_disabled: loading,
              onClick: loadItems,
            },
            export: {
              label: "Export Items",
              is_disabled: loading,
              options: {
                formats: ["csv", "excel", "json"],
                filename: "menu-items-list",
              },
            },
          }}
          emptyState={{
            icon: "restaurant_menu",
            title: "No Menu Items Found",
            description: search
              ? "No menu items match your search criteria. Try adjusting your filters or search term."
              : "Get started by adding your first menu item.",
            actionLabel: "Add Menu Item",
            onAction: handleCreateItem,
          }}
        />
      </Paper>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDeleteItem}
        title="Delete Menu Item"
        itemName={selectedItem?.name || ""}
        itemDescription={selectedItem?.description || ""}
        loading={saving}
        icon="restaurant_menu"
      />

      {/* Toggle Availability Dialog */}
      <Dialog
        open={toggleDialogOpen}
        onClose={handleCloseToggleDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 4px 16px rgba(8,145,178,0.10)",
            p: 0,
            fontFamily: "Montserrat",
          },
        }}
      >
        <DialogTitle sx={{ pb: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Icon
              sx={{
                color: !selectedItemForToggle?.is_available
                  ? "#059669"
                  : "#DC2626",
                fontSize: 32,
              }}
            >
              {!selectedItemForToggle?.is_available ? "check_circle" : "cancel"}
            </Icon>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Montserrat",
                fontWeight: 600,
                color: "#1E293B",
              }}
            >
              {!selectedItemForToggle?.is_available
                ? "Enable Menu Item"
                : "Disable Menu Item"}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 1 }}>
          <Typography
            sx={{ fontFamily: "Montserrat", color: "#334155", fontWeight: 500 }}
          >
            Are you sure you want to{" "}
            {!selectedItemForToggle?.is_available ? "enable" : "disable"} the
            menu item "{selectedItemForToggle?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleCloseToggleDialog}
            sx={{
              borderColor: "#0891B2",
              color: "#0891B2",
              fontFamily: "Montserrat",
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
              "&:hover": {
                borderColor: "#0891B2",
                bgcolor: "#F0F9FF",
              },
            }}
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={handleToggleAvailability}
            disabled={
              !hasPermission(
                "inventory.menu_items.edit",
                user?.newPermissions,
                user?.assignment_details?.id,
                user?.is_store_owner
              )
            }
            loading={saving}
            variant="contained"
            sx={{
              bgcolor: !selectedItemForToggle?.is_available
                ? "#059669"
                : "#DC2626",
              color: "#fff",
              fontFamily: "Montserrat",
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
              "&:hover": {
                bgcolor: !selectedItemForToggle?.is_available
                  ? "#047857"
                  : "#B91C1C",
              },
            }}
          >
            {!selectedItemForToggle?.is_available ? "Enable" : "Disable"}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Details Drawer */}
      {detailsDrawerOpen && selectedItem && (
        <MenuItemDetailsDrawer
          open={detailsDrawerOpen}
          onClose={handleDetailsClose}
          item={selectedItem}
        />
      )}
    </Box>
  );
};
